import { useNavigate } from "react-router-dom";
import { Check, ChevronLeft, X } from "lucide-react";
import SciensHaloLogo from "../utils/SciensHaloLogo.svg";
import { useState } from "react";
import Button from "../utils/Button";
import apiCall from "../network/api";
import OtpScreen from "../components/OtpScreen";
import { Modal } from "../utils/Modal";

const ObcSignup = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [isValidEmail, setIsValidEmail] = useState(null);
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [otpError, setOtpError] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [signupLoader, setSignupLoader] = useState(false);
  const [verificationLoader, setVerificationLoader] = useState(false);
  const [toast, setToast] = useState({
    isOpen: false,
    message: "",
  });
  const [isOtpVerified, setIsOtpVerified] = useState(false);

  const handleCloseToast = () => {
    setToast({ isOpen: false, message: "" });
  };

  const validateEmail = (email) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(String(email).toLowerCase());
  };

  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    if (newEmail.length > 0) {
      setIsValidEmail(validateEmail(newEmail));
    } else {
      setIsValidEmail(null);
    }
  };

  const generateOtp = async (isResend) => {
    setSignupLoader(true);
    try {
      await apiCall({
        endpoint: "/otp/request-otp",
        method: "POST",
        data: {
          emailId: email,
          isResend,
          emailData: {
            customerName: email.trim().split("@")[0],
            status: "otp",
            emailTo: email,
            emailCc: "",
            emailBcc: "",
          },
        },
      });

      setIsOtpSent(true);
      setToast({
        isOpen: true,
        message: isResend
          ? "New verification code sent! Please check your email."
          : "OTP sent to your email address. Please check your spam folder if not received.",
      });
    } catch (err) {
      setOtpError(err?.data?.message);
    } finally {
      setSignupLoader(false);
    }
  };

  const handleVerify = async ({ emailId, otp, updateData, emailData }) => {
    setVerificationLoader(true);
    try {
      await apiCall({
        endpoint: "/otp/verify-otp",
        method: "POST",
        data: {
          emailId: emailId,
          otp: otp?.join(""),
          emailData: emailData,
        },
      });
      setIsModalOpen(true);
    } catch (err) {
      setOtpError(err?.data?.message);
    } finally {
      setVerificationLoader(false);
    }
  };

  return (
    <div className="min-h-screen bg-white p-8 flex flex-col justify-between">
      <div className="max-w-md mx-auto">
        <div className="mb-8">
          <div className="mb-8">
            <img
              src={SciensHaloLogo}
              alt="SciensLogistics Halo Logo"
              className="h-14"
            />
          </div>
          {!isOtpVerified ? (
            <button
              onClick={() => navigate("/")}
              className="flex items-center text-gray-500 text-sm mb-6"
            >
              <ChevronLeft className="w-5 h-5 mr-1" />
              Back
            </button>
          ) : (
            <></>
          )}
        </div>

        {!isOtpVerified ? (
          <div>
            <div>
              <h1 className="text-2xl font-bold text-gray-500 mb-4">
                {isOtpVerified ? "Application form" : "Sign up"}{" "}
              </h1>
              <p className="text-gray-400 text-sm mb-8">
                {isOtpVerified ? (
                  "Fill in the required details below."
                ) : (
                  <div>
                    We appreciate your interest! <br />
                    Complete the form below - we'll verify and contact eligible
                    applicants.
                  </div>
                )}
              </p>

              {otpError ? (
                <div className="bg-customError-100 ring-1 ring-customError-200 rounded-md p-3 mb-4">
                  <p className="text-sm font-semibold text-customError-500">
                    {otpError}
                  </p>
                </div>
              ) : (
                <></>
              )}

              {
                !isOtpSent ? (
                  <>
                    <div className="mb-6">
                      <div className="relative">
                        <input
                          value={email}
                          onChange={handleEmailChange}
                          type="email"
                          placeholder="Enter your email"
                          className={`w-full p-3 rounded-lg bg-gray-50 border border-gray-200 focus:outline-none focus:ring-1 ${
                            isValidEmail === true
                              ? "focus:ring-primary-500"
                              : isValidEmail === false
                                ? "focus:ring-customError-500"
                                : "ring-gray-300"
                          }`}
                        />
                        {isValidEmail !== null && (
                          <span className="absolute inset-y-0 right-0 pr-3 flex items-center">
                            {isValidEmail ? (
                              <Check className="h-5 w-5 text-primary-500" />
                            ) : (
                              <X className="h-5 w-5 text-customError-500" />
                            )}
                          </span>
                        )}
                      </div>
                    </div>

                    {otpError ? (
                      <Button
                        text={"Go to Login"}
                        size={"md"}
                        onClick={() => navigate("/login")}
                      />
                    ) : (
                      <Button
                        text={"Proceed"}
                        size={"md"}
                        disabled={!isValidEmail}
                        onClick={() => generateOtp(false)}
                        loading={signupLoader}
                      />
                    )}
                  </>
                ) : (
                  <OtpScreen
                    emailId={email}
                    handleResend={() => generateOtp(true)}
                    handleVerify={handleVerify}
                    error={otpError}
                    setError={setOtpError}
                    verificationLoader={verificationLoader}
                    handleCloseToast={handleCloseToast}
                    toast={toast}
                    setToast={setToast}
                  />
                )
                // ) : (
                //   <ObcOnboarding />
                // )
              }
            </div>
            <Modal
              isSuccess={true}
              isOpen={isModalOpen}
              successHeader={"Welcome Aboard!"}
              successMessage={
                <p className="text-left space-y-1.5">
                  <p>
                    To complete your onboarding process, please keep these
                    ready:
                  </p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Valid passport (6+ months validity)</li>
                    <li>CV (PDF)</li>
                    <li>Passport scans front and back both</li>
                    <li>Phone Number</li>
                  </ul>
                  <p>Ready to begin?</p>
                </p>
              }
              successButton={"Begin Onboarding"}
              successButtonAction={() => {
                setIsModalOpen(false);
                setIsOtpVerified(true);
                localStorage.setItem("onboardingUserEmail", email);
              }}
            />
          </div>
        ) : (
          // <ObcOnboarding/>
          navigate("/onboarding", { state: { email: email } })
        )}
      </div>
      <div className="mt-12 text-center">
        <p className="text-gray-500 text-sm">© Sciens Logistics LLC</p>
      </div>
    </div>
  );
};

export default ObcSignup;
